import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Drawer,
  Tag,
  Table,
  Button,
  Space,
  Typography,
  Card,
  Row,
  Col,
  Popconfirm,
  message,
  Select,
  Modal,
  Spin,
  Empty,
} from "antd";
import {
  TeamOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { TTraceableInstanceGroup } from "../../../models/traceabilityModel";
import traceabilityService from "../../../services/traceabilityService";

const { Title, Text } = Typography;

interface Props {
  visible: boolean;
  onClose: () => void;
  group: TTraceableInstanceGroup | null;
  tokenData: any;
  companyUid: string;
  availableInstances?: any[];
  onRefresh: () => void;
}

export const ViewGroupDetailModal: React.FC<Props> = ({
  visible,
  onClose,
  group,
  tokenData,
  companyUid,
  availableInstances = [],
  onRefresh,
}) => {
  const [addMembersModalVisible, setAddMembersModalVisible] = useState(false);
  const [selectedInstanceIds, setSelectedInstanceIds] = useState<number[]>([]);
  const [loadingAction, setLoadingAction] = useState(false);
  const [loadedInstances, setLoadedInstances] = useState<any[]>([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchInstances = useCallback(
    async (searchTerm?: string) => {
      if (!group?.company_id && !companyUid) return;
      try {
        setLoadingInstances(true);
        const res = await traceabilityService.getInstances(tokenData, {
          company_id: group?.company_id,
          company_uid: companyUid,
          search: searchTerm?.trim() || undefined,
          limit: 100,
        });
        if (res && res.instances) {
          setLoadedInstances((prev) => {
            if (searchTerm && searchTerm.trim()) {
              // Merge searched instances with previous to avoid losing selected items
              const map = new Map<number, any>();
              prev.forEach((item) => map.set(item.id, item));
              res.instances.forEach((item: any) => map.set(item.id, item));
              return Array.from(map.values());
            }
            return res.instances;
          });
        }
      } catch (err) {
        console.error("Error fetching instances for group:", err);
      } finally {
        setLoadingInstances(false);
      }
    },
    [group?.company_id, companyUid, tokenData]
  );

  useEffect(() => {
    if (addMembersModalVisible) {
      setSelectedInstanceIds([]);
      fetchInstances();
    }
  }, [addMembersModalVisible, fetchInstances]);

  if (!group) return null;

  const members = group.traceable_instance_group_members || [];
  const activeMembers = members.filter((m) => m.is_active);
  const linkedCycles = group.production_cycle_traceable_link || [];

  const handleDebouncedSearch = (searchText: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchInstances(searchText);
    }, 350);
  };

  const handleAddMembers = async () => {
    try {
      if (selectedInstanceIds.length === 0) return;
      setLoadingAction(true);
      const res = await traceabilityService.addGroupMembers(tokenData, {
        group_id: group.id,
        instance_ids: selectedInstanceIds,
      });
      if (res.error) throw new Error(res.message);
      message.success("Miembros agregados al grupo con éxito");
      setSelectedInstanceIds([]);
      setAddMembersModalVisible(false);
      onRefresh();
    } catch (err: any) {
      message.error(err.message || "Error al agregar miembros");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleRemoveMember = async (instanceId: number) => {
    try {
      const res = await traceabilityService.removeGroupMembers(tokenData, {
        group_id: group.id,
        instance_ids: [instanceId],
      });
      if (res.error) throw new Error(res.message);
      message.success("Miembro removido del grupo");
      onRefresh();
    } catch (err: any) {
      message.error(err.message || "Error al remover miembro");
    }
  };

  // Combine instances from fetch and prop, eliminating duplicates
  const instancePoolMap = new Map<number, any>();
  (availableInstances || []).forEach((inst) => instancePoolMap.set(inst.id, inst));
  loadedInstances.forEach((inst) => instancePoolMap.set(inst.id, inst));

  const activeMemberIds = new Set(activeMembers.map((m) => m.instance_id));
  const selectableInstances = Array.from(instancePoolMap.values()).filter(
    (inst) => !activeMemberIds.has(inst.id)
  );

  const instanceSelectOptions = selectableInstances.map((inst) => {
    const currentActiveGroupMember = inst.traceable_instance_group_members?.find(
      (m: any) => m.is_active
    );
    const otherGroupName = currentActiveGroupMember?.traceable_instance_groups?.name;

    const details = [
      inst.name,
      inst.storage_elements?.name,
      inst.module_options?.string_value,
      otherGroupName ? `Grupo actual: ${otherGroupName}` : null,
    ]
      .filter(Boolean)
      .join(" • ");

    return {
      value: inst.id,
      label: `${inst.identifier}${details ? ` (${details})` : ""}`,
      searchText: `${inst.identifier} ${inst.name || ""} ${inst.storage_elements?.name || ""} ${inst.module_options?.string_value || ""}`.toLowerCase(),
    };
  });

  const columns = [
    {
      title: "Identificador",
      key: "identifier",
      render: (_: any, record: any) => {
        const inst = record.traceable_instances;
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{inst?.identifier || `ID #${record.instance_id}`}</Text>
            {inst?.name && <Text type="secondary" style={{ fontSize: 12 }}>{inst.name}</Text>}
          </Space>
        );
      },
    },
    {
      title: "Producto Padre",
      key: "element",
      render: (_: any, record: any) => {
        const elem = record.traceable_instances?.storage_elements;
        return elem?.name ? <Tag color="blue">{elem.name}</Tag> : <Text type="secondary">-</Text>;
      },
    },
    {
      title: "Estado",
      key: "status",
      render: (_: any, record: any) => {
        const status = record.traceable_instances?.status || "ACTIVE";
        return <Tag color={status === "ACTIVE" ? "green" : "orange"}>{status}</Tag>;
      },
    },
    {
      title: "Fecha Ingreso Grupo",
      key: "joined_at",
      render: (_: any, record: any) => dayjs(record.joined_at).format("DD/MM/YYYY"),
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_: any, record: any) => (
        <Popconfirm
          title="¿Remover del grupo?"
          description="El elemento dejará de pertenecer a este grupo pero mantendrá su historial."
          onConfirm={() => handleRemoveMember(record.instance_id)}
          okText="Sí, remover"
          cancelText="Cancelar"
        >
          <Button size="small" danger icon={<UserDeleteOutlined />}>
            Remover
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <Drawer
        open={visible}
        onClose={onClose}
        width={760}
        title={
          <Space align="center">
            <TeamOutlined style={{ color: "#722ed1", fontSize: 22 }} />
            <div>
              <Title level={5} style={{ margin: 0 }}>
                {group.name}
              </Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {group.module_options?.string_value || "Grupo Trazable"} • Creado el{" "}
                {dayjs(group.created_at).format("DD/MM/YYYY")}
              </Text>
            </div>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => setAddMembersModalVisible(true)}
          >
            Agregar Miembros
          </Button>
        }
      >
        <Card size="small" style={{ marginBottom: 16, backgroundColor: "#fafafa" }}>
          <Row gutter={16} align="middle">
            <Col xs={12} sm={6}>
              <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                ESTADO
              </Text>
              <Tag color={group.status === "ACTIVE" ? "green" : "default"}>{group.status}</Tag>
            </Col>
            <Col xs={12} sm={6}>
              <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                MIEMBROS ACTIVOS
              </Text>
              <Text strong style={{ color: "#722ed1", fontSize: 16 }}>
                {activeMembers.length}
              </Text>
            </Col>
            <Col xs={12} sm={12}>
              <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                CICLOS VINCULADOS
              </Text>
              {linkedCycles.length > 0 ? (
                linkedCycles.map((l) => (
                  <Tag key={l.id} color="cyan">
                    <LinkOutlined /> {l.production_cycles?.name || `Ciclo #${l.cycle_id}`}
                  </Tag>
                ))
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Ningún ciclo de manufactura vinculado
                </Text>
              )}
            </Col>
          </Row>
          {group.description && (
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">{group.description}</Text>
            </div>
          )}
        </Card>

        <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Title level={5} style={{ margin: 0 }}>
            Miembros del Grupo ({activeMembers.length})
          </Title>
        </div>

        <Table
          dataSource={activeMembers}
          columns={columns}
          rowKey="id"
          size="middle"
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No hay miembros activos en este grupo" }}
        />
      </Drawer>

      <Modal
        open={addMembersModalVisible}
        title="Agregar Miembros al Grupo"
        onCancel={() => setAddMembersModalVisible(false)}
        onOk={handleAddMembers}
        confirmLoading={loadingAction}
        okText="Agregar al Grupo"
        cancelText="Cancelar"
      >
        <div style={{ padding: "12px 0" }}>
          <Text style={{ display: "block", marginBottom: 8 }}>
            Seleccione uno o más elementos trazables para agregar a <strong>{group.name}</strong>:
          </Text>
          <Select
            mode="multiple"
            showSearch
            style={{ width: "100%" }}
            placeholder="Buscar por identificador, nombre o producto..."
            value={selectedInstanceIds}
            onChange={(vals) => setSelectedInstanceIds(vals)}
            onSearch={handleDebouncedSearch}
            filterOption={(input, option) => {
              if (!input) return true;
              const term = input.toLowerCase();
              const labelText = (option?.label as string) || "";
              const searchContent = (option as any)?.searchText || "";
              return labelText.toLowerCase().includes(term) || searchContent.includes(term);
            }}
            options={instanceSelectOptions}
            loading={loadingInstances}
            notFoundContent={
              loadingInstances ? (
                <div style={{ textAlign: "center", padding: "12px 0" }}>
                  <Spin size="small" /> Cargando elementos...
                </div>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    activeMembers.length > 0 && selectableInstances.length === 0
                      ? "Todos los elementos disponibles ya forman parte de este grupo"
                      : "No se encontraron elementos trazables disponibles"
                  }
                />
              )
            }
            allowClear
            maxTagCount="responsive"
          />
        </div>
      </Modal>
    </>
  );
};

export default ViewGroupDetailModal;